import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import Home from './components/Home';
import ProductsPage from './components/ProductsPage';
import FragmentsPage from './components/FragmentsPage';
import LatestPage from './components/LatestPage';
import LookbookPage from './components/LookbookPage';
import MagazinePage from './components/MagazinePage';
import MagazineDetail from './components/MagazineDetail';
import ProductDetail from './components/ProductDetail';
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './components/admin/AdminLogin';
import AdminInventory from './components/admin/AdminInventory';
import AdminOrders from './components/admin/AdminOrders';
import AdminVault from './components/admin/AdminVault';
import AdminContent from './components/admin/AdminContent';
import AdminEmail from './components/admin/AdminEmail';
import AdminBootstrap from './components/admin/AdminBootstrap';
import VaultPage from './components/vault/VaultPage';
import VaultItemDetail from './components/vault/VaultItemDetail';
import ClubView from './components/profile/ClubView';
import OrderDetail from './components/profile/OrderDetail';
import OrdersView from './components/profile/OrdersView';
import SettingsView from './components/profile/SettingsView';
import ArticleList from './components/ArticleList';
import ArticleDetail from './components/ArticleDetail';
import EventList from './components/EventList';
import EventDetail from './components/EventDetail';
import SupportPage from './components/SupportPage';
import { Product } from './types';
import StripeReturn from './components/StripeReturn';
import UnsubscribePage from './components/UnsubscribePage';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { StripeConfigProvider } from './context/StripeConfigContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import AdminSettings from './components/admin/AdminSettings';
import AuthPage from './components/auth/AuthPage';
import './utils/firebase';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ProductDetailWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = React.useState<Product | undefined>();
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./utils/firebase');
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() } as Product);
        } else {
           console.error("Product not found in dynamic DB");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return <div className="min-h-screen pt-24 flex justify-center"><div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div></div>;
  }

  if (!product) {
    return <Navigate to="/" replace />;
  }

  return (
    <ProductDetail 
      product={product} 
      onRelatedClick={(p) => navigate(`/product/${p.id}`)}
    />
  );
};

const App: React.FC = () => {
  const navigate = useNavigate();

  const handleProductClick = (product: Product) => {
    navigate(`/product/${product.id}`);
  };

  return (
    <AuthProvider>
      <FeatureFlagsProvider>
      <StripeConfigProvider>
        <CartProvider>
          <ScrollToTop />
          <Routes>
          {/* Public Routes with App Shell */}
          <Route element={<AppLayout />}>
            <Route path="/" element={<Home onProductClick={handleProductClick} />} />

            <Route path="/latest" element={<LatestPage onProductClick={handleProductClick} />} />

            {/* SHOP SECTION */}
            <Route path="/shop" element={
              <ProductsPage
                title="AVAILABLE"
                subtitle="Open now. Everything is made when claimed."
                heroImage="/assets/placeholder.svg"
                onProductClick={handleProductClick}
                mode="now"
                pageId="shop"
              />
            } />

            <Route path="/fragments" element={
              <FragmentsPage onProductClick={handleProductClick} />
            } />

            <Route path="/shop/soon" element={
              <ProductsPage
                title="Surfacing soon"
                heroImage="/assets/placeholder.svg"
                onProductClick={handleProductClick}
                mode="soon"
                pageId="shop-soon"
              />
            } />

            <Route path="/standard-issue" element={
              <ProductsPage
                title="STANDARD ISSUE"
                subtitle="The crew left no merchandise. These are blank by design."
                heroImage="/assets/placeholder.svg"
                onProductClick={handleProductClick}
                tierFilter="uniform"
                pageId="uniform"
              />
            } />
            {/* Legacy path — the tier was renamed customer-side; tier value stays 'uniform' internally */}
            <Route path="/uniform" element={<Navigate to="/standard-issue" replace />} />

            <Route path="/artifacts" element={
              <ProductsPage
                title="ARTIFACTS"
                subtitle="Artifacts surface rarely. One of each exists."
                heroImage="/assets/placeholder.svg"
                onProductClick={handleProductClick}
                tierFilter="artifact"
                pageId="artifacts"
              />
            } />

            <Route path="/vault" element={<VaultPage />} />
            <Route path="/vault/:id" element={<VaultItemDetail />} />
            {/* <Route path="/lookbook" element={<LookbookPage />} /> */}
            <Route path="/magazine" element={<MagazinePage />} />
            <Route path="/magazine/:id" element={<MagazineDetail />} />

            <Route path="/product/:id" element={<ProductDetailWrapper />} />

            <Route path="/success" element={<StripeReturn />} />
            <Route path="/cancel" element={<StripeReturn />} />
            
            {/* PUBLIC AUTH */}
            <Route path="/login" element={<AuthPage initialMode="login" />} />
            <Route path="/join" element={<AuthPage initialMode="signup" />} />
            
            {/* PROFILE SECTION */}
            <Route path="/profile" element={<div className="pb-20"><Outlet /></div>}>
               <Route index element={<ClubView />} />
               <Route path="orders" element={<OrdersView />} />
               <Route path="orders/:id" element={<OrderDetail />} />
               <Route path="settings" element={<SettingsView />} />
            </Route>

            {/* Content Pages (To be implemented in Phase 4) */}
            {/* Content Pages */}
            <Route path="/story" element={<ArticleList type="story" title="Stories" />} />
            {/* <Route path="/lookbook" element={<ArticleList type="lookbook" title="Lookbooks" />} /> */}
            <Route path="/news" element={<ArticleList type="news" title="News" />} />
            {/* <Route path="/articles" element={<ArticleList title="All Articles" />} /> */}
            <Route path="/article/:id" element={<ArticleDetail />} />
            
            <Route path="/events" element={<EventList />} />
            <Route path="/event/:id" element={<EventDetail />} />
            
            <Route path="/support" element={<SupportPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
          </Route>
          
          {/* Admin Routes */}
          <Route path="/admin/bootstrap" element={<AdminBootstrap />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/inventory" replace />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="email" element={<AdminEmail />} />
            <Route path="vault" element={<AdminVault />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </CartProvider>
      </StripeConfigProvider>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
};

export default App;
